/**
 * AP-10 Nachtrag 5, 5. — **E-Mail-Signatur im Profil.**
 *
 * Steht unter jeder Mail aus dem Exportversand. Leer heißt: Die Vorgabe aus Name,
 * Funktion, Firma, Telefon und E-Mail gilt — sie ändert sich mit dem Profil mit.
 * Belegmails tragen keine Signatur; dort unterschreiben die Platzhalter des
 * Sachbearbeiters.
 *
 * AP-36 Phase E: ohne Knopf. Der Text speichert beim Verlassen des Felds, die
 * beiden Schaltflächen („Vorgabe übernehmen", „Zurücksetzen") sind Handlungen
 * und speichern sofort.
 */

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MAIL_EXPORT_LIMITS, type MailSignatureView } from '@symphenia/shared-types'
import { fehlertext } from '@/lib/fehlertext'
import { api } from '@/lib/axios'
import { mailApi } from '@/lib/mail/mail.api'
import {
  FeldStatus,
  mitStand,
  useFeld,
  useSpeicherstelle,
  useStellenDaten,
  type Datensatz,
  type Speicherstelle,
} from '@/lib/speichern'

export function MailSignatureCard() {
  const { t } = useTranslation()
  const [geladen, setGeladen] = useState<Datensatz | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)

  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: 'mailSignature',
    bezeichnung: t('mailSignature.title'),
    daten: geladen,
    schreibe: (patch, stand) => api.put<Datensatz>('/mail/signature', patch, mitStand(stand)).then((r) => r.data),
    keepalive: { url: '/mail/signature', method: 'PUT' },
  }) as Speicherstelle<unknown>

  useEffect(() => {
    mailApi
      .getSignature()
      .then(({ data }) => setGeladen(data as unknown as Datensatz))
      .catch((err) => setFehler(fehlertext(err, t)))
  }, [t])

  return (
    <div className="rounded-lg border bg-card p-6 shadow-sm">
      <h2 className="text-base font-semibold text-foreground">{t('mailSignature.title')}</h2>
      <p className="mb-4 mt-1 text-sm text-muted-foreground">{t('mailSignature.intro')}</p>

      {fehler && <div className="mt-3 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{fehler}</div>}
      {geladen && <Signatur stelle={stelle} />}
    </div>
  )
}

function Signatur({ stelle }: { stelle: Speicherstelle<unknown> }) {
  const { t } = useTranslation()
  const daten = useStellenDaten(stelle) as unknown as MailSignatureView
  const feld = useFeld<string | null>(stelle, 'signature', {
    label: t('mailSignature.title'),
    // Leer heißt „die Vorgabe gilt" — der Server macht daraus `null`.
    lies: (x) => (x.trim() === '' ? null : x),
    schreib: (w) => w ?? '',
    pruefe: (w) =>
      (w ?? '').replace(/\r\n/g, '\n').split('\n').length > MAIL_EXPORT_LIMITS.signatureLines
        ? t('mail.signature.tooManyLines')
        : null,
  })
  const leer = !(feld.wert ?? '').trim()

  return (
    <>
      <textarea
        className="w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring aria-[invalid=true]:border-destructive"
        rows={6}
        maxLength={MAIL_EXPORT_LIMITS.signature}
        aria-label={t('mailSignature.title')}
        {...feld.props}
      />
      <FeldStatus status={feld} />

      {leer && (
        <div className="mt-3 rounded-md bg-muted/50 p-3">
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('mailSignature.defaultTitle')}</p>
          <pre className="whitespace-pre-wrap font-sans text-sm text-foreground">
            {daten.defaultSignature || t('mailSignature.defaultEmpty')}
          </pre>
        </div>
      )}

      <div className="mt-4 flex flex-wrap justify-end gap-2">
        {leer && daten.defaultSignature && (
          <button
            type="button"
            onClick={() => feld.setze(daten.defaultSignature)}
            className="rounded-md border border-border bg-background px-4 py-2 text-sm hover:bg-card"
          >
            {t('mailSignature.useDefault')}
          </button>
        )}
        {daten.signature && (
          <button
            type="button"
            onClick={() => feld.setze(null)}
            className="rounded-md border border-border bg-background px-4 py-2 text-sm hover:bg-card"
          >
            {t('mailSignature.reset')}
          </button>
        )}
      </div>
    </>
  )
}
